import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import {
  planStatusTransition,
  type ManualStatusTarget,
  type StatusTransitionPlan,
} from "@/lib/order-status-transition";
import { fanOutDeliveredToLegs } from "@/lib/deliver-legs-fanout";
import { fanOutInTransitToLegs } from "@/lib/in-transit-legs-fanout";
import { settleTrailerAfterOrderClose } from "@/lib/trailer-settle";
import { notifyOrderStatus } from "@/lib/notify-order-status";

interface TransitionOrder {
  id: string;
  unique_id?: string | null;
  status?: unknown;
  trailer_id?: string | null;
  parent_order_id?: string | null;
  actual_departure_time?: string | null;
  actual_arrival_time?: string | null;
}

/**
 * Explicit "Mark In Transit" / "Mark Delivered" order-page actions.
 *
 * Replaces the edit form's status dropdown (Phase 4). The form now only
 * writes order data; every status move is its own deliberate action with
 * its own gating, audit row and downstream fan-out. No EAPI call is made —
 * these are local promotions that mirror what the driver app / EAPI poller
 * would otherwise report.
 */
export function useOrderStatusTransition(order: TransitionOrder | null | undefined) {
  const { tenantId, activeTenant } = useTenant();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const queryClient = useQueryClient();
  const userEmail = user?.email ?? null;

  const planFor = (target: ManualStatusTarget): StatusTransitionPlan =>
    planStatusTransition(target, {
      currentStatus: order?.status,
      isAdmin: !!isAdmin,
      actualDepartureTime: order?.actual_departure_time ?? null,
      actualArrivalTime: order?.actual_arrival_time ?? null,
    });

  const mutation = useMutation({
    mutationFn: async (target: ManualStatusTarget) => {
      if (!order?.id) throw new Error("No order loaded");
      const plan = planFor(target);
      if (!plan.allowed) throw new Error(plan.reason);
      const previousStatus = String(order.status ?? "") || null;

      let query = supabase.from("orders").update(plan.patch as never).eq("id", order.id);
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { error } = await query;
      if (error) throw error;

      // Keep the leg rows in lockstep with a manually delivered parent, then
      // release the trailer. Same helpers the edit-form path used.
      if (target === "in_transit") {
        await fanOutInTransitToLegs({
          orderId: order.id,
          tenantId,
          changedBy: userEmail || "Unknown",
        });
      }
      if (target === "delivered") {
        await fanOutDeliveredToLegs({
          orderId: order.id,
          tenantId,
          changedBy: userEmail || "Unknown",
        });
        await settleTrailerAfterOrderClose({
          orderId: order.id,
          trailerHumanId: order.trailer_id ?? null,
          tenantId,
          outcome: "delivered",
        });
      }

      // Audit row. Best-effort: a logging failure must not undo the promotion.
      try {
        await supabase.from("order_change_log").insert({
          order_id: order.id,
          changed_by: userEmail || "Unknown",
          field_name: "status",
          old_value: previousStatus,
          new_value: target,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        } as never);
      } catch (logErr) {
        console.warn("[useOrderStatusTransition] change log insert failed", logErr);
      }

      // Customer notification — root orders only, eligibility enforced in send-email.
      if (!order.parent_order_id) {
        void notifyOrderStatus({
          orderId: order.id,
          newStatus: target,
          initiator: "dispatcher-ui",
          tenantSlug: activeTenant,
          previousStatus,
        });
      }

      return target;
    },
    onSuccess: (target) => {
      toast.success(
        target === "delivered" ? "Order marked delivered" : "Order marked in transit",
      );
      queryClient.invalidateQueries({ queryKey: ["order-detail", order?.id] });
      queryClient.invalidateQueries({ queryKey: ["order-children-legs", order?.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Could not update the order status");
    },
  });

  return {
    planFor,
    markInTransit: () => mutation.mutate("in_transit"),
    markDelivered: () => mutation.mutate("delivered"),
    isPending: mutation.isPending,
  };
}
